/**
 * Debug FAB and menu for admin users.
 * Provides quick access to shipment monitor, action logs, and CSV export.
 */

import React, { useState } from "react";
import { Fab, Menu, MenuItem } from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import type { ShipmentEventApi } from "@services/shipments/types";
import { exportShipmentEventsToCSV } from "../utils/csvHelpers";

interface DebugMenuProps {
  shipmentId: string;
  events: ShipmentEventApi[];
  isAdmin: boolean;
}

/**
 * Debug FAB with dropdown menu for admin actions.
 * Only renders when isAdmin is true.
 */
export function DebugMenu({ shipmentId, events, isAdmin }: DebugMenuProps) {
  const [debugMenuAnchor, setDebugMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  if (!isAdmin) return null;

  const handleDebugMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDebugMenuAnchor(event.currentTarget);
  };

  const handleDebugMenuClose = () => {
    setDebugMenuAnchor(null);
  };

  const handleOpenShipmentMonitor = () => {
    window.open(
      `/www/admin/shipments-dashboard-monitor?search=${encodeURIComponent(shipmentId)}`,
      "_blank"
    );
    handleDebugMenuClose();
  };

  const handleOpenActionLogs = () => {
    const shipmentTypes = [
      "CreateShipment",
      "UpdateShipment",
      "GptRequest",
      "MergeShipment",
      "Analysis",
    ].join(",");

    window.open(
      `/www/admin/action-logs?derivedDataId=${shipmentId}&types=${encodeURIComponent(shipmentTypes)}&viewMode=list&includeDescription=true&displayErrorsInline=true`,
      "_blank"
    );
    handleDebugMenuClose();
  };

  const handleExportToCSV = () => {
    exportShipmentEventsToCSV(events, shipmentId);
    handleDebugMenuClose();
  };

  return (
    <>
      <Fab
        size="small"
        aria-label="debug"
        onClick={handleDebugMenuOpen}
        sx={{
          color: "#1976d2",
          position: "fixed",
          bottom: 66,
          right: 16,
          zIndex: 1000,
        }}
      >
        <BugReportIcon />
      </Fab>

      <Menu
        anchorEl={debugMenuAnchor}
        open={Boolean(debugMenuAnchor)}
        onClose={handleDebugMenuClose}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleOpenShipmentMonitor}>
          Open in Shipment Monitor
        </MenuItem>
        <MenuItem onClick={handleOpenActionLogs}>See list of updates</MenuItem>
        <MenuItem onClick={handleExportToCSV}>Export to CSV</MenuItem>
      </Menu>
    </>
  );
}
